if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.dialog = this.querySelector('[data-product-lightbox]');
        this.content = this.querySelector('.product-media-modal__content');
        this.previousButton = this.querySelector('[data-lightbox-previous]');
        this.nextButton = this.querySelector('[data-lightbox-next]');
        this.closeButton = this.querySelector('[id^="ModalClose-"]');
        this.onResize = this.fitActiveMedia.bind(this);
        if (!this.dialog || !this.content) return;

        this.previousButton.addEventListener('click', () => this.changeMedia(-1));
        this.nextButton.addEventListener('click', () => this.changeMedia(1));
        this.dialog.addEventListener('click', (event) => {
          if (event.target.closest('button, [data-media-id].active')) return;
          this.hide();
        });
        this.dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.hide();
        });
        this.dialog.addEventListener('close', () => {
          if (!this.dialog.open) this.hide();
        });
        this.dialog.addEventListener('keydown', this.onKeyDown.bind(this));
        this.dialog.addEventListener('keyup', (event) => {
          if (event.key !== 'Escape' || !this.hasAttribute('open')) return;
          event.preventDefault();
          event.stopPropagation();
          this.hide();
        });
        this.content.addEventListener('touchstart', (event) => {
          this.swipeStart = null;
          if (!this.hasAttribute('open') || event.touches.length !== 1 ||
              !event.target.closest('img[data-media-id].active')) return;
          const touch = event.touches[0];
          this.swipeStart = { id: touch.identifier, x: touch.clientX, y: touch.clientY };
        }, { passive: true });
        this.content.addEventListener('touchend', this.onSwipeEnd.bind(this), { passive: true });
        this.content.addEventListener('touchcancel', () => { this.swipeStart = null; }, { passive: true });
      }

      connectedCallback() {
        const owner = this.closest('quick-add-modal');
        if (owner) owner.productLightbox = this;
        super.connectedCallback();
      }

      /** @param {HTMLElement} opener - Existing gallery button that opened this modal. */
      show(opener) {
        if (!this.dialog || !this.content || this.hasAttribute('open')) return;
        const requestedId = opener?.getAttribute('data-media-id');
        this.mediaItems = Array.from(this.content.children).filter((element, index) =>
          element.hasAttribute('data-media-id') &&
          (!element.classList.contains('product__media-item--variant') || index === 0 ||
            element.getAttribute('data-media-id') === requestedId)
        );
        const requestedIndex = this.mediaItems.findIndex((element) =>
          element.getAttribute('data-media-id') === requestedId
        );
        if (!this.mediaItems.length || requestedIndex < 0) return;

        this.openedBy = opener;
        this.scrollPosition = { left: window.scrollX, top: window.scrollY };
        this.bodyWasLocked = document.body.classList.contains('overflow-hidden');
        document.body.classList.add('overflow-hidden');
        this.setAttribute('open', '');
        this.activeIndex = requestedIndex;
        this.showActiveMedia();
        this.nativeDialog = typeof this.dialog.showModal === 'function';
        if (this.nativeDialog) this.dialog.showModal();
        else this.dialog.setAttribute('open', '');
        this.fitActiveMedia();
        this.closeButton.focus({ preventScroll: true });
        window.addEventListener('resize', this.onResize);
      }

      hide() {
        if (!this.hasAttribute('open')) return;
        this.swipeStart = null;
        this.removeAttribute('open');
        if (this.nativeDialog && this.dialog.open) this.dialog.close();
        else this.dialog.removeAttribute('open');
        if (!this.bodyWasLocked) document.body.classList.remove('overflow-hidden');
        window.removeEventListener('resize', this.onResize);
        window.pauseAllMedia();
        if (this.openedBy?.isConnected) this.openedBy.focus({ preventScroll: true });
        // The lightbox never switches or scrolls the underlying product gallery.
        window.scrollTo({ ...this.scrollPosition, behavior: 'instant' });
        document.body.dispatchEvent(new CustomEvent('modalClosed'));
      }

      disconnectedCallback() {
        this.hide();
      }

      /** @param {number} direction - Previous (-1) or next (1) media. */
      changeMedia(direction) {
        if (!this.hasAttribute('open') || this.mediaItems.length < 2) return;
        this.activeIndex = (this.activeIndex + direction + this.mediaItems.length) % this.mediaItems.length;
        this.showActiveMedia();
      }


      /** @param {TouchEvent} event - Horizontal swipes on photos only; preserve pinch and vertical gestures. */
      onSwipeEnd(event) {
        const start = this.swipeStart;
        this.swipeStart = null;
        if (!start || !this.hasAttribute('open') || event.touches.length) return;
        const touch = Array.from(event.changedTouches).find((item) => item.identifier === start.id);
        if (!touch) return;
        const dx = touch.clientX - start.x;
        const dy = touch.clientY - start.y;
        if (Math.abs(dx) >= 45 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          this.changeMedia(dx < 0 ? 1 : -1);
        }
      }

      showActiveMedia() {
        window.pauseAllMedia();
        Array.from(this.content.children).forEach((element) => element.classList.remove('active'));
        const activeMedia = this.mediaItems[this.activeIndex];
        activeMedia.classList.add('active');
        this.querySelector('[data-lightbox-current]').textContent = String(this.activeIndex + 1);
        this.querySelector('[data-lightbox-total]').textContent = String(this.mediaItems.length);
        this.previousButton.hidden = this.nextButton.hidden = this.mediaItems.length < 2;
        this.fitActiveMedia();

        // Keep Dawn's deferred poster/playback rather than replacing video markup.
        const template = activeMedia.querySelector('template');
        if (activeMedia.matches('deferred-media') && template?.content.querySelector('.js-youtube')) {
          activeMedia.loadContent(false);
        }
      }

      fitActiveMedia() {
        if (!this.hasAttribute('open') || !this.mediaItems) return;
        const activeMedia = this.mediaItems[this.activeIndex];
        if (activeMedia.tagName === 'IMG') return;
        const poster = activeMedia.querySelector('.deferred-media__poster img');
        const ratio = poster ? Number(poster.getAttribute('width')) / Number(poster.getAttribute('height')) : 1;
        const safeRatio = Number.isFinite(ratio) && ratio > 0 ? ratio : 1;
        const width = Math.min(this.content.clientWidth, this.content.clientHeight * safeRatio);
        activeMedia.style.width = `${width}px`;
        activeMedia.style.height = `${width / safeRatio}px`;
      }

      /** @param {KeyboardEvent} event */
      onKeyDown(event) {
        if (!this.hasAttribute('open')) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        if (!this.nativeDialog && event.key === 'Tab') this.trapFallbackFocus(event);
        if (event.target.closest('video, iframe, model-viewer, input, select, textarea')) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.changeMedia(event.key === 'ArrowLeft' ? -1 : 1);
        }
      }

      /** @param {KeyboardEvent} event - Legacy browsers without native dialog support. */
      trapFallbackFocus(event) {
        const focusable = getFocusableElements(this.dialog).filter((element) =>
          element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden'
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if ((!event.shiftKey && event.target === last) || (event.shiftKey && event.target === first)) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus({ preventScroll: true });
        }
      }
    }
  );
}
